import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  Chip,
  Alert,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountTree as TreeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Custom node component for condition nodes
const ConditionNode = ({ data, selected }) => {
  const theme = useTheme();
  return (
    <Paper
      sx={{
        p: 2,
        minWidth: 250,
        maxWidth: 350,
        bgcolor: selected ? 'primary.light' : 'background.paper',
        border: 2,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        borderRadius: 2,
        boxShadow: selected ? 4 : 2,
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Chip label="Condition" size="small" color="primary" />
          <Box>
            {data.onEdit && (
              <Tooltip title="Click to edit">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    data.onEdit(data);
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 500,
            wordBreak: 'break-word',
            color: theme.palette.text.primary,
          }}
        >
          {data.label || '(no condition)'}
        </Typography>
        {data.description && (
          <Typography variant="caption" color="text.secondary">
            {data.description}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

// Custom node component for leaf/outcome nodes
const LeafNode = ({ data, selected }) => {
  const theme = useTheme();
  const getOutcomeColor = (outcome) => {
    if (outcome === 'unknown' || !outcome) return 'default';
    if (outcome.toLowerCase().includes('exempt')) return 'success';
    if (outcome.toLowerCase().includes('defer')) return 'warning';
    return 'success';
  };

  return (
    <Paper
      sx={{
        p: 2,
        minWidth: 250,
        maxWidth: 350,
        bgcolor: selected ? 'primary.light' : 'background.paper',
        border: 2,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        borderRadius: 2,
        boxShadow: selected ? 4 : 2,
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Chip
            label="Outcome"
            size="small"
            color={getOutcomeColor(data.outcome)}
          />
          <Box>
            {data.onEdit && (
              <Tooltip title="Click to edit">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    data.onEdit(data);
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {data.outcome || 'unknown'}
        </Typography>
        {data.message && (
          <Typography variant="body2" color="text.secondary">
            {data.message}
          </Typography>
        )}
        {data.missing_fields && data.missing_fields.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Missing: {data.missing_fields.join(', ')}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

const nodeTypes = {
  condition: ConditionNode,
  leaf: LeafNode,
};

function DecisionTreeEditorReactFlow({ model, onSave, onClose }) {
  const [tree, setTree] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeMap, setNodeMap] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Convert tree structure to ReactFlow nodes and edges
  const convertTreeToFlow = useCallback((treeRoot, x = 400, y = 50, depth = 0, parentId = null, branch = null) => {
    const flowNodes = [];
    const flowEdges = [];
    const nodeId = `node-${Date.now()}-${Math.random()}`;
    
    if (!treeRoot) return { nodes: [], edges: [] };

    if (treeRoot.type === 'leaf') {
      flowNodes.push({
        id: nodeId,
        type: 'leaf',
        position: { x, y },
        data: {
          label: treeRoot.outcome || 'unknown',
          outcome: treeRoot.outcome || 'unknown',
          message: treeRoot.message || '',
          missing_fields: treeRoot.missing_fields || [],
          originalNode: treeRoot,
          onEdit: (nodeData) => handleEditNode(nodeData),
        },
      });
      
      if (parentId) {
        flowEdges.push({
          id: `edge-${parentId}-${nodeId}`,
          source: parentId,
          target: nodeId,
          type: 'smoothstep',
          animated: true,
          label: branch === 'true' ? 'TRUE' : branch === 'false' ? 'FALSE' : '',
          style: { stroke: branch === 'true' ? '#4caf50' : branch === 'false' ? '#f44336' : '#2196f3' },
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }
    } else if (treeRoot.type === 'condition') {
      flowNodes.push({
        id: nodeId,
        type: 'condition',
        position: { x, y },
        data: {
          label: treeRoot.condition || '(no condition)',
          condition: treeRoot.condition || '',
          originalNode: treeRoot,
          onEdit: (nodeData) => handleEditNode(nodeData),
        },
      });

      if (parentId) {
        flowEdges.push({
          id: `edge-${parentId}-${nodeId}`,
          source: parentId,
          target: nodeId,
          type: 'smoothstep',
          animated: true,
          label: branch === 'true' ? 'TRUE' : branch === 'false' ? 'FALSE' : '',
          style: { stroke: branch === 'true' ? '#4caf50' : branch === 'false' ? '#f44336' : '#2196f3' },
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }

      // Recursively process true and false branches
      const spacing = 400;
      const childY = y + 150;
      const trueX = x - spacing / 2;
      const falseX = x + spacing / 2;

      if (treeRoot.true) {
        const trueBranch = convertTreeToFlow(treeRoot.true, trueX, childY, depth + 1, nodeId, 'true');
        flowNodes.push(...trueBranch.nodes);
        flowEdges.push(...trueBranch.edges);
      }

      if (treeRoot.false) {
        const falseBranch = convertTreeToFlow(treeRoot.false, falseX, childY, depth + 1, nodeId, 'false');
        flowNodes.push(...falseBranch.nodes);
        flowEdges.push(...falseBranch.edges);
      }
    }

    return { nodes: flowNodes, edges: flowEdges };
  }, []);

  useEffect(() => {
    if (model?.decision_tree?.root) {
      const treeData = JSON.parse(JSON.stringify(model.decision_tree));
      setTree(treeData);
      const { nodes: flowNodes, edges: flowEdges, nodeMap: map } = convertTreeToFlow(treeData.root);
      setNodes(flowNodes);
      setEdges(flowEdges);
      setNodeMap(map);
    }
  }, [model, convertTreeToFlow, setNodes, setEdges]);

  const handleEditNode = useCallback((nodeData) => {
    setSelectedNode(nodeData);
    setEditDialogOpen(true);
  }, []);

  const handleSaveNode = () => {
    if (!selectedNode) return;

    // Find and update the node in the tree
    const updateNodeInTree = (node, targetId) => {
      if (node.data?.originalNode === selectedNode.originalNode) {
        // Update the original node data
        Object.assign(node.data.originalNode, {
          ...node.data.originalNode,
          ...selectedNode,
        });
        return true;
      }
      
      if (node.type === 'condition' && node.data?.originalNode) {
        const orig = node.data.originalNode;
        if (orig.true) {
          const trueResult = updateNodeInTree(
            { data: { originalNode: orig.true } },
            targetId
          );
          if (trueResult) return true;
        }
        if (orig.false) {
          return updateNodeInTree(
            { data: { originalNode: orig.false } },
            targetId
          );
        }
      }
      return false;
    };

    // Update in flow nodes
    setNodes((nds) =>
      nds.map((node) => {
        if (node.data?.originalNode === selectedNode.originalNode) {
          return {
            ...node,
            data: {
              ...node.data,
              ...selectedNode,
              originalNode: { ...node.data.originalNode, ...selectedNode },
            },
          };
        }
        return node;
      })
    );

    // Rebuild tree from nodes (simplified - in production, maintain tree structure separately)
    setEditDialogOpen(false);
    setSelectedNode(null);
  };

  const handleSaveTree = async () => {
    setSaving(true);
    setError(null);

    try {
      // Reconstruct tree from nodes (simplified version)
      // In a full implementation, you'd want to maintain the tree structure
      // and sync it with the visual representation
      
      const response = await fetch(`/api/v1/policy/models/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision_tree: tree }),
      });

      if (response.ok) {
        if (onSave) onSave();
        if (onClose) onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save tree');
      }
    } catch (err) {
      setError(`Error saving tree: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node.data);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onNodesDelete = useCallback((deleted) => {
    // Handle node deletion
    // This would need to update the tree structure accordingly
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TreeIcon color="primary" />
            <Typography variant="h6">Decision Tree Editor</Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CheckIcon /> : <SaveIcon />}
              onClick={handleSaveTree}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Tree'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, position: 'relative', display: 'flex' }}>
        {/* Side Panel for Node Details */}
        {selectedNode && (
          <Paper
            sx={{
              width: 350,
              p: 2,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Node Details
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<CheckIcon />}
                onClick={() => handleEditNode(selectedNode)}
              >
                Edit Node
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => {
                  // Add child node functionality
                  alert('Add node functionality - to be implemented');
                }}
              >
                Add Child Node
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<DeleteIcon />}
                onClick={() => {
                  if (window.confirm('Delete this node?')) {
                    // Delete node functionality
                    alert('Delete node functionality - to be implemented');
                  }
                }}
              >
                Delete Node
              </Button>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Node Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {selectedNode.originalNode?.type || selectedNode.type || 'unknown'}
                </Typography>
                {selectedNode.condition && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Condition: {selectedNode.condition}
                  </Typography>
                )}
                {selectedNode.outcome && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Outcome: {selectedNode.outcome}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        )}
        
        <Box sx={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.2}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              markerEnd: { type: MarkerType.ArrowClosed },
            }}
            connectionLineStyle={{ stroke: '#2196f3', strokeWidth: 2 }}
            deleteKeyCode="Delete"
          >
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                if (node.type === 'condition') return '#2196f3';
                if (node.type === 'leaf') return '#4caf50';
                return '#757575';
              }}
            />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </Box>
      </Box>

      {/* Edit Node Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedNode(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Node</DialogTitle>
        <DialogContent>
          {selectedNode && (
            <NodeEditForm
              node={selectedNode}
              onChange={(updated) => setSelectedNode({ ...selectedNode, ...updated })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setSelectedNode(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveNode} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function NodeEditForm({ node, onChange }) {
  const [localNode, setLocalNode] = useState(node);

  useEffect(() => {
    setLocalNode(node);
  }, [node]);

  const updateField = (field, value) => {
    const updated = { ...localNode, [field]: value };
    setLocalNode(updated);
    if (onChange) onChange(updated);
  };

  if (node.originalNode?.type === 'leaf' || node.type === 'leaf' || node.outcome !== undefined) {
    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          label="Outcome"
          value={localNode.outcome || ''}
          onChange={(e) => updateField('outcome', e.target.value)}
          placeholder="e.g., Residential Permit, Exempt, etc."
        />
        <TextField
          fullWidth
          label="Message"
          value={localNode.message || ''}
          onChange={(e) => updateField('message', e.target.value)}
          multiline
          rows={2}
        />
        <TextField
          fullWidth
          label="Missing Fields (comma-separated)"
          value={Array.isArray(localNode.missing_fields) ? localNode.missing_fields.join(', ') : ''}
          onChange={(e) => {
            const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
            updateField('missing_fields', fields);
          }}
          placeholder="field1, field2, field3"
        />
      </Stack>
    );
  }

  if (node.originalNode?.type === 'condition' || node.type === 'condition' || node.condition !== undefined) {
    return (
      <Stack spacing={3} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          label="Condition"
          value={localNode.condition || localNode.label || ''}
          onChange={(e) => {
            updateField('condition', e.target.value);
            updateField('label', e.target.value);
          }}
          placeholder="e.g., property_type == 'residential'"
          helperText="Examples: field == 'value', field > 100, exists:field"
          multiline
          rows={3}
        />
      </Stack>
    );
  }

  return null;
}

export default DecisionTreeEditorReactFlow;

